/**
 * Subscription Widget - Syncs custom Petlio-style UI with Seal Subscriptions
 */
(function () {
  'use strict';

  const SW = {
    sellingPlanData: null,
    currentVariantId: null,
    selectedPlanId: null,
    isSubscription: true,

    init() {
      this.widget = document.querySelector('.subscription-widget');
      if (!this.widget) return;

      // Read translatable strings from data attributes
      this.strings = {
        badge: this.widget.dataset.badgeText || 'SAVE',
        perUnit: this.widget.dataset.perUnitLabel || 'Bottle',
      };

      this.parseSellingPlanData();
      this.cacheElements();
      this.bindEvents();
      this.setupPriceObserver();
      this.syncInitialState();
    },

    parseSellingPlanData() {
      const dataEl = document.querySelector('[data-sw-selling-plans]');
      if (!dataEl) return;
      try {
        this.sellingPlanData = JSON.parse(dataEl.textContent);
      } catch (e) {
        console.warn('Subscription widget: Could not parse selling plan data');
      }
    },

    cacheElements() {
      this.subscribeCard = this.widget.querySelector('[data-sw-option="subscribe"]');
      this.onetimeCard = this.widget.querySelector('[data-sw-option="onetime"]');
      this.frequencySelect = this.widget.querySelector('[data-sw-frequency]');
      this.frequencyText = this.widget.querySelector('[data-sw-frequency-text]');
      this.frequencyLabel = this.widget.querySelector('.sw-frequency-label');
      this.subscribePriceEl = this.widget.querySelector('[data-sw-subscribe-price]');
      this.onetimePriceEl = this.widget.querySelector('[data-sw-onetime-price]');
      this.originalPriceEl = this.widget.querySelector('[data-sw-original-price]');
      this.subscribePerUnitEl = this.widget.querySelector('[data-sw-subscribe-per-unit]');
      this.onetimePerUnitEl = this.widget.querySelector('[data-sw-onetime-per-unit]');
      this.badgeEl = this.widget.querySelector('.sw-badge');
      this.sellingPlanInput = document.querySelector('input[name="selling_plan"]');
      this.variantInput = document.querySelector('product-details input[name="id"]');
    },

    bindEvents() {
      // Card selection
      this.subscribeCard?.addEventListener('click', (e) => {
        if (e.target.closest('.sw-frequency-select')) return;
        this.selectOption('subscribe');
      });

      this.onetimeCard?.addEventListener('click', () => {
        this.selectOption('onetime');
      });

      // Frequency change
      this.frequencySelect?.addEventListener('change', () => {
        if (this.isSubscription) {
          this.selectedPlanId = parseInt(this.frequencySelect.value);
          this.updatePrices();
          this.syncToSeal();
          this.updateSellingPlanInput();
        }
      });

      // Intercept form submit to ensure selling_plan is correct
      this.setupFormIntercept();

      // Listen for variant changes from the theme
      this.setupVariantObserver();

      // Listen for Seal Subscriptions events
      document.addEventListener('sealsubs:subscription_widget_created', () => {
        this.syncToSeal();
        this.updateSellingPlanInput();
      });

      document.addEventListener('sealsubs:price_update', (e) => {
        // Seal sent a price update; re-sync our selling plan input
        this.updateSellingPlanInput();
      });

      document.addEventListener('sealsubs:selling_plan_changed', (e) => {
        // Seal changed the plan internally; re-sync our value
        this.updateSellingPlanInput();
      });
    },

    variantHasSubscription(variantId) {
      if (!this.sellingPlanData || !variantId) return false;
      const allocations = this.sellingPlanData.allocations[variantId];
      return allocations && allocations.length > 0;
    },

    updateWidgetVisibility() {
      const hasPlans = this.variantHasSubscription(this.currentVariantId);

      this.widget.style.display = hasPlans ? '' : 'none';

      if (!hasPlans && this.isSubscription) {
        // Force one-time mode when variant has no plans
        this.isSubscription = false;
        this.selectedPlanId = null;
        this.subscribeCard?.classList.remove('sw-card--active');
        this.onetimeCard?.classList.add('sw-card--active');
        const subscribeRadio = this.subscribeCard?.querySelector('input[type="radio"]');
        const onetimeRadio = this.onetimeCard?.querySelector('input[type="radio"]');
        if (subscribeRadio) subscribeRadio.checked = false;
        if (onetimeRadio) onetimeRadio.checked = true;
        this.updateSellingPlanInput();
        this.syncToSeal();
      } else if (hasPlans && !this.isSubscription) {
        // Re-select subscribe when switching back to a variant with plans
        this.selectOption('subscribe');
      }
    },

    onVariantChange() {
      this.updateWidgetVisibility();
      if (this.variantHasSubscription(this.currentVariantId)) {
        this.updateFrequencyOptions();
        this.updatePrices();
      }
      this.syncToSeal();
      this.updateSellingPlanInput();
    },

    setupVariantObserver() {
      if (!this.variantInput) return;

      // Watch for variant ID changes via MutationObserver
      const observer = new MutationObserver(() => {
        const newVariantId = this.variantInput.value;
        if (newVariantId && newVariantId !== String(this.currentVariantId)) {
          this.currentVariantId = parseInt(newVariantId);
          this.onVariantChange();
        }
      });

      observer.observe(this.variantInput, {
        attributes: true,
        attributeFilter: ['value']
      });

      // Intercept programmatic .value changes — read the current descriptor
      // (which may already be overridden by variant-price-updater)
      const currentDescriptor =
        Object.getOwnPropertyDescriptor(this.variantInput, 'value') ||
        Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      if (currentDescriptor) {
        const widget = this;
        const prevSet = currentDescriptor.set;
        const prevGet = currentDescriptor.get;
        try {
          Object.defineProperty(this.variantInput, 'value', {
            get() {
              return prevGet.call(this);
            },
            set(val) {
              prevSet.call(this, val);
              const numVal = parseInt(val);
              if (numVal && numVal !== widget.currentVariantId) {
                widget.currentVariantId = numVal;
                requestAnimationFrame(() => {
                  widget.onVariantChange();
                });
              }
            },
            configurable: true
          });
        } catch (e) {
          // Fallback: polling
          setInterval(() => {
            const val = parseInt(this.variantInput.value);
            if (val && val !== this.currentVariantId) {
              this.currentVariantId = val;
              this.onVariantChange();
            }
          }, 300);
        }
      }
    },

    updateFrequencyOptions() {
      if (!this.frequencySelect || !this.sellingPlanData) return;

      const allocations = this.sellingPlanData.allocations[this.currentVariantId] || [];
      const validPlanIds = allocations.map(a => String(a.selling_plan_id));

      // Rebuild select options from the variant's actual allocations
      this.frequencySelect.innerHTML = '';
      for (const planId of validPlanIds) {
        let planName = planId;
        for (const group of (this.sellingPlanData.groups || [])) {
          const found = group.plans.find(p => String(p.id) === planId);
          if (found) { planName = found.name; break; }
        }
        const option = document.createElement('option');
        option.value = planId;
        option.textContent = planName;
        this.frequencySelect.appendChild(option);
      }

      if (validPlanIds.length === 1) {
        // Single plan: hide dropdown and label, show full plan name as text
        const planId = validPlanIds[0];
        let planName = '';
        for (const group of (this.sellingPlanData.groups || [])) {
          const found = group.plans.find(p => String(p.id) === planId);
          if (found) { planName = found.name; break; }
        }

        this.frequencySelect.style.display = 'none';
        if (this.frequencyLabel) this.frequencyLabel.style.display = 'none';
        if (this.frequencyText) {
          this.frequencyText.textContent = planName;
          this.frequencyText.style.display = '';
        }

        this.frequencySelect.value = planId;
        this.selectedPlanId = parseInt(planId);
      } else if (validPlanIds.length > 1) {
        // Multiple plans: show dropdown and label, hide text
        this.frequencySelect.style.display = '';
        if (this.frequencyLabel) this.frequencyLabel.style.display = '';
        if (this.frequencyText) {
          this.frequencyText.style.display = 'none';
        }

        this.frequencySelect.value = validPlanIds[0];
        this.selectedPlanId = parseInt(validPlanIds[0]);
      }
    },

    syncInitialState() {
      // Set initial variant
      if (this.variantInput) {
        this.currentVariantId = parseInt(this.variantInput.value);
      } else if (window.currentVariant) {
        this.currentVariantId = window.currentVariant.id;
      }

      // Filter frequency options for initial variant
      this.updateFrequencyOptions();

      // Set initial selling plan
      if (this.frequencySelect) {
        this.selectedPlanId = parseInt(this.frequencySelect.value);
      }

      // Check if initial variant has subscription plans
      this.isSubscription = true;
      this.updateWidgetVisibility();
      if (this.variantHasSubscription(this.currentVariantId)) {
        this.updatePrices();
      }
      this.updateSellingPlanInput();

      // Protect the selling_plan input from Seal overwriting its value
      this.protectSellingPlanInput();

      // Give Seal time to load, then sync
      setTimeout(() => { this.syncToSeal(); this.updateSellingPlanInput(); }, 500);
      setTimeout(() => { this.syncToSeal(); this.updateSellingPlanInput(); }, 1500);
    },

    selectOption(option) {
      this.isSubscription = option === 'subscribe';

      // Update card active states
      this.subscribeCard?.classList.toggle('sw-card--active', this.isSubscription);
      this.onetimeCard?.classList.toggle('sw-card--active', !this.isSubscription);

      // Update radio inputs
      const subscribeRadio = this.subscribeCard?.querySelector('input[type="radio"]');
      const onetimeRadio = this.onetimeCard?.querySelector('input[type="radio"]');
      if (subscribeRadio) subscribeRadio.checked = this.isSubscription;
      if (onetimeRadio) onetimeRadio.checked = !this.isSubscription;

      if (this.isSubscription) {
        this.selectedPlanId = parseInt(this.frequencySelect?.value);
      } else {
        this.selectedPlanId = null;
      }

      this.updatePrices();
      this.syncToSeal();
      this.updateSellingPlanInput();
      this.updateMainPrice();
      this.updateButtonPrice();
    },

    updatePrices() {
      if (!this.sellingPlanData || !this.currentVariantId) return;

      const allocations = this.sellingPlanData.allocations[this.currentVariantId];
      if (!allocations) return;

      // Find the current variant's base price
      const variant = window.product?.variants?.find(v => v.id == this.currentVariantId);
      const basePrice = variant?.price;
      if (!basePrice) return;

      // Get bottles count for per-unit pricing
      const bottles = this.sellingPlanData.bottles?.[this.currentVariantId] || 0;

      // Update one-time price
      if (this.onetimePriceEl) {
        this.onetimePriceEl.textContent = this.formatMoney(basePrice);
      }

      // Update one-time per-unit price
      if (this.onetimePerUnitEl) {
        if (bottles > 1) {
          const perUnit = Math.round(basePrice / bottles);
          this.onetimePerUnitEl.textContent = '(' + this.formatMoney(perUnit) + '/' + this.strings.perUnit + ')';
          this.onetimePerUnitEl.style.display = '';
        } else {
          this.onetimePerUnitEl.style.display = 'none';
        }
      }

      // Find allocation for selected plan
      if (this.selectedPlanId && allocations.length > 0) {
        const alloc = allocations.find(a => a.selling_plan_id == this.selectedPlanId) || allocations[0];
        const subPrice = alloc.price;
        const comparePrice = alloc.compare_at_price || basePrice;

        if (this.subscribePriceEl) {
          this.subscribePriceEl.textContent = this.formatMoney(subPrice);
        }

        if (this.originalPriceEl) {
          if (subPrice < comparePrice) {
            this.originalPriceEl.textContent = this.formatMoney(comparePrice);
            this.originalPriceEl.style.display = '';
          } else {
            this.originalPriceEl.style.display = 'none';
          }
        }

        // Update subscribe per-unit price
        if (this.subscribePerUnitEl) {
          if (bottles > 1) {
            const perUnit = Math.round(subPrice / bottles);
            this.subscribePerUnitEl.textContent = '(' + this.formatMoney(perUnit) + '/' + this.strings.perUnit + ')';
            this.subscribePerUnitEl.style.display = '';
          } else {
            this.subscribePerUnitEl.style.display = 'none';
          }
        }

        // Update badge
        if (this.badgeEl && comparePrice > 0 && subPrice < comparePrice) {
          const pct = Math.round(((comparePrice - subPrice) / comparePrice) * 100);
          this.badgeEl.textContent = this.strings.badge + ' ' + pct + '%';
          this.badgeEl.style.display = '';
        }
      }

      this.updateMainPrice();
      this.updateButtonPrice();
    },

    setupPriceObserver() {
      const sectionId = this.widget.dataset.sectionId;
      const priceContainer = document.querySelector('#price-' + sectionId);
      if (!priceContainer) return;

      this._priceObserverPaused = false;
      this._priceObserver = new MutationObserver(() => {
        if (this._priceObserverPaused) return;
        // Theme re-rendered the price; re-apply our update after it settles
        clearTimeout(this._priceObserverTimer);
        this._priceObserverTimer = setTimeout(() => {
          this.updateMainPrice();
        }, 50);
      });

      this._priceObserver.observe(priceContainer, {
        childList: true,
        subtree: true,
        characterData: true
      });
    },

    updateMainPrice() {
      const priceContainer = document.querySelector('#price-' + this.widget.dataset.sectionId);
      if (!priceContainer) return;

      const priceEl = priceContainer.querySelector('.price');
      if (!priceEl) return;

      // Pause observer to avoid infinite loop
      this._priceObserverPaused = true;

      // Find compare-at price element (could be <s>, <del>, or .compare-at-price)
      const compareEl = priceContainer.querySelector('s, del, .compare-at-price');

      if (this.isSubscription && this.selectedPlanId) {
        const allocations = this.sellingPlanData?.allocations[this.currentVariantId];
        if (allocations && allocations.length > 0) {
          const alloc = allocations.find(a => a.selling_plan_id == this.selectedPlanId) || allocations[0];
          priceEl.textContent = this.formatMoney(alloc.price);
          // Hide compare_at_price since our widget already shows it
          if (compareEl) compareEl.style.display = 'none';
        }
      } else {
        // One-time: show regular variant price
        const variant = window.product?.variants?.find(v => v.id == this.currentVariantId);
        if (variant) {
          priceEl.textContent = this.formatMoney(variant.price);
          // Restore compare_at_price visibility for one-time
          if (compareEl) compareEl.style.display = '';
        }
      }

      // Resume observer after our changes settle
      requestAnimationFrame(() => {
        this._priceObserverPaused = false;
      });
    },

    updateButtonPrice() {
      const btnText = document.querySelector('.btn--text[data-add-to-cart-text]') || document.querySelector('.btn--text');
      if (!btnText) return;

      const variant = window.product?.variants?.find(v => v.id == this.currentVariantId);
      if (!variant) return;

      let price = variant.price;

      if (this.isSubscription && this.selectedPlanId) {
        const allocations = this.sellingPlanData?.allocations[this.currentVariantId];
        if (allocations) {
          const alloc = allocations.find(a => a.selling_plan_id == this.selectedPlanId) || allocations[0];
          price = alloc.price;
        }
      }

      // Check quantity
      const qtyInput = document.querySelector('input[name="quantity"]');
      const qty = parseInt(qtyInput?.value || 1);
      let totalPrice = price * qty;

      // Apply quantity discounts (matching variant-price-updater.js logic)
      if (qty >= 5) {
        totalPrice = totalPrice * 0.6;
      } else if (qty >= 3) {
        totalPrice = totalPrice * 0.7;
      }

      // Rebuild the full button innerHTML to work with protectButtonContent
      const defaultText = btnText.closest('[data-add-to-cart-text]')?.dataset.defaultText || btnText.querySelector('.btn--text-label')?.textContent?.trim() || '+ ADD TO CART';
      btnText.innerHTML =
        '<span class="btn--text-label">' + defaultText + '</span>' +
        '<span class="btn--text-price" data-price="' + totalPrice + '"> - ' + this.formatMoney(totalPrice) + '</span>';
    },

    setupFormIntercept() {
      const form = document.querySelector('product-details .product-form form');
      if (!form) return;

      // Use CAPTURE phase so this runs BEFORE ProductForm's bubble-phase handler
      form.addEventListener('submit', () => {
        const planValue = this.isSubscription && this.selectedPlanId ? String(this.selectedPlanId) : '';

        // Remove any duplicate selling_plan inputs (e.g. added by Seal)
        const allInputs = form.querySelectorAll('input[name="selling_plan"]');
        allInputs.forEach((input, i) => {
          if (i === 0) {
            input.value = planValue;
          } else {
            input.remove();
          }
        });

        // If no selling_plan input exists, create one
        if (allInputs.length === 0) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          input.value = planValue;
          form.appendChild(input);
        }
      }, true); // ← capture phase: fires before ProductForm's bubble-phase handler

      // Watch for Seal adding/modifying selling_plan inputs
      this._formObserver = new MutationObserver(() => {
        const allInputs = form.querySelectorAll('input[name="selling_plan"]');
        if (allInputs.length > 1) {
          for (let i = 1; i < allInputs.length; i++) {
            allInputs[i].remove();
          }
        }
        // Re-sync our value
        if (allInputs[0]) {
          const expected = this.isSubscription && this.selectedPlanId ? String(this.selectedPlanId) : '';
          if (allInputs[0].value !== expected) {
            allInputs[0].value = expected;
          }
        }
      });

      this._formObserver.observe(form, { childList: true, subtree: true });
    },

    /**
     * Protect the selling_plan input's .value property from being overwritten
     * by Seal Subscriptions (programmatic .value = '' can't be caught by MutationObserver)
     */
    protectSellingPlanInput() {
      const form = document.querySelector('product-details .product-form form');
      if (!form) return;

      const ensureInput = () => {
        let input = form.querySelector('input[name="selling_plan"]');
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'selling_plan';
          form.appendChild(input);
        }
        return input;
      };

      const widget = this;
      const input = ensureInput();
      const descriptor =
        Object.getOwnPropertyDescriptor(input, 'value') ||
        Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');

      if (descriptor) {
        try {
          Object.defineProperty(input, 'value', {
            get() {
              return descriptor.get.call(this);
            },
            set(val) {
              // If Seal tries to clear or change the value, override with our expected value
              const expected = widget.isSubscription && widget.selectedPlanId ? String(widget.selectedPlanId) : '';
              descriptor.set.call(this, expected);
            },
            configurable: true
          });
        } catch (e) {
          // Fallback: poll and fix
          setInterval(() => {
            const inp = form.querySelector('input[name="selling_plan"]');
            if (inp) {
              const expected = widget.isSubscription && widget.selectedPlanId ? String(widget.selectedPlanId) : '';
              if (inp.value !== expected) {
                inp.value = expected;
              }
            }
          }, 200);
        }
      }
    },

    updateSellingPlanInput() {
      const form = document.querySelector('product-details .product-form form');
      if (!form) return;

      // Remove any duplicate selling_plan inputs Seal may have added
      const allInputs = form.querySelectorAll('input[name="selling_plan"]');
      if (allInputs.length > 1) {
        for (let i = 1; i < allInputs.length; i++) {
          allInputs[i].remove();
        }
      }

      this.sellingPlanInput = allInputs[0];
      if (!this.sellingPlanInput) {
        this.sellingPlanInput = document.createElement('input');
        this.sellingPlanInput.type = 'hidden';
        this.sellingPlanInput.name = 'selling_plan';
        form.appendChild(this.sellingPlanInput);
      }

      // Use the native setter to bypass our own protection for legitimate updates
      const descriptor =
        Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value');
      const expected = this.isSubscription && this.selectedPlanId ? this.selectedPlanId : '';
      if (descriptor) {
        descriptor.set.call(this.sellingPlanInput, expected);
      } else {
        this.sellingPlanInput.value = expected;
      }
    },

    syncToSeal() {
      // Dispatch event to Seal Subscriptions to sync the selected plan
      const planId = this.isSubscription && this.selectedPlanId ? this.selectedPlanId : 0;

      document.dispatchEvent(new CustomEvent('sealsubs:change_selected_selling_plan_id', {
        detail: { selling_plan_id: planId }
      }));
    },

    formatMoney(cents) {
      if (window.Shopify?.formatMoney && window.theme?.moneyFormat) {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      }
      const amount = (cents / 100).toFixed(2);
      const currency = window.Shopify?.currency?.active || 'EUR';
      const symbols = { USD: '$', EUR: '\u20AC', GBP: '\u00A3', CAD: '$' };
      const symbol = symbols[currency] || '';
      return symbol ? symbol + amount : amount + ' ' + currency;
    }
  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => SW.init());
  } else {
    SW.init();
  }
})();
